import { useCallback } from 'react';

/**
 * Lightweight haptic feedback for touch devices via the Vibration API
 * (navigator.vibrate). Desktop is a no-op; only touch-primary/coarse-pointer
 * clients get a subtle tap. Respects prefers-reduced-motion and hidden tabs.
 */

const vibrate: ((pattern: number | number[]) => boolean) | undefined =
  typeof navigator !== 'undefined' && 'vibrate' in navigator ? navigator.vibrate.bind(navigator) : undefined;

const isDocumentVisible = () => typeof document !== 'undefined' && document.visibilityState !== 'hidden';

const isTouchPrimary = () =>
  typeof window !== 'undefined' &&
  (navigator.maxTouchPoints ?? 0) > 0 &&
  window.matchMedia('(pointer: coarse)').matches;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Predefined intensities. */
export const HAPTIC: Record<'light' | 'medium' | 'firm', number[]> = {
  /** Light tap — icon toggles, quick-search. */
  light: [8],
  /** Standard press — buttons, accordion open/close. */
  medium: [18],
  /** Firm — destructive / primary actions (logout, collapse rail). */
  firm: [30],
};

/**
 * Fire a haptic tap. Uses short, distinct patterns so strong vs subtle
 * actions feel different. Returns true when a vibration was actually
 * triggered.
 */
export function haptic(pattern: number | number[]): boolean {
  if (!vibrate || !isDocumentVisible() || !isTouchPrimary() || prefersReducedMotion()) {
    return false;
  }
  return vibrate(pattern);
}

/**
 * React hook exposing a stable haptic callback to attach to a press handler.
 * Usage: const tap = useHaptic('medium'); <button onClick={() => { tap(); ... }}>
 */
export function useHaptic(intensity: keyof typeof HAPTIC = 'medium'): () => boolean {
  return useCallback(() => haptic(HAPTIC[intensity]), [intensity]);
}
